import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Grid,
  Divider,
  Tooltip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/firebase";

interface IJob {
  title: string;
  company: string;
  location: string;
  period: string;
  description: string;
  achievements: string[];
  techStack: string[];
  priority: number;
}

const Experience = () => {
  const [jobs, setJobs] = useState<IJob[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchExperience = async () => {
      const snapshot = await getDocs(collection(db, "experience"));
      const data = snapshot.docs
        .map((doc) => doc.data() as IJob)
        .sort((a, b) => a.priority - b.priority);
      setJobs(data);
      setLoading(false);
    };

    fetchExperience();
  }, []);

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        px: { xs: 3, md: 10 },
        py: { xs: 6, md: 8 },
      }}
    >
      <Typography
        variant="overline"
        sx={{ color: "#64ffda", letterSpacing: "4px", fontSize: "0.85rem" }}
      >
        Where I've worked
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 1,
          mb: 8,
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 700,
            color: "#ffffff",
            fontSize: { xs: "2rem", md: "2.8rem" },
          }}
        >
          Experience
        </Typography>

        <Tooltip title="Download CV" placement="left">
          <IconButton
            component="a"
            href="/assets/BradleyRushforth.pdf"
            download
            sx={{
              color: "#64ffda",
              border: "1px solid rgba(100, 255, 218, 0.25)",
              borderRadius: 2,
              p: 1,
              "&:hover": {
                backgroundColor: "rgba(100, 255, 218, 0.08)",
                borderColor: "rgba(100, 255, 218, 0.6)",
              },
            }}
          >
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ position: "relative" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <CircularProgress sx={{ color: "#64ffda" }} />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                position: "absolute",
                left: { xs: 16, md: 24 },
                top: 0,
                bottom: 0,
                width: "2px",
                background:
                  "linear-gradient(to bottom, #64ffda 0%, rgba(100,255,218,0.15) 100%)",
              }}
            />

            {jobs.map((job, index) => (
              <Box
                key={index}
                sx={{
                  position: "relative",
                  pl: { xs: "52px", md: "72px" },
                  pb: index < jobs.length - 1 ? { xs: 8, md: 10 } : 0,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    left: { xs: "9px", md: "15px" },
                    top: "4px",
                    width: { xs: 16, md: 16 },
                    height: { xs: 16, md: 16 },
                    borderRadius: "50%",
                    backgroundColor: "#64ffda",
                    boxShadow: "0 0 12px rgba(100, 255, 218, 0.6)",
                    border: "2px solid #0a192f",
                  }}
                />

                <Grid container spacing={4} alignItems="flex-start">
                  <Grid size={{ xs: 12, lg: 7 }}>
                    <Chip
                      label={job.period}
                      size="small"
                      sx={{
                        mb: 1.5,
                        backgroundColor: "rgba(100, 255, 218, 0.08)",
                        color: "#64ffda",
                        border: "1px solid rgba(100, 255, 218, 0.25)",
                        fontSize: "0.72rem",
                        letterSpacing: "1px",
                        fontFamily: "monospace",
                      }}
                    />

                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: 700,
                        color: "#ccd6f6",
                        fontSize: { xs: "1.2rem", md: "1.45rem" },
                        mb: 0.5,
                      }}
                    >
                      {job.title}
                    </Typography>

                    <Typography
                      sx={{
                        color: "#64ffda",
                        fontSize: "0.95rem",
                        fontWeight: 500,
                        mb: 0.5,
                      }}
                    >
                      {job.company}
                      <Box
                        component="span"
                        sx={{ color: "#a8b2d8", fontWeight: 400, ml: 1.5 }}
                      >
                        · {job.location}
                      </Box>
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        color: "#a8b2d8",
                        lineHeight: 1.8,
                        fontSize: "0.97rem",
                        maxWidth: 720,
                        mt: 2,
                        mb: 3,
                      }}
                    >
                      {job.description}
                    </Typography>

                    <Typography
                      variant="overline"
                      sx={{
                        color: "#64ffda",
                        letterSpacing: "3px",
                        fontSize: "0.7rem",
                        display: "block",
                        mb: 1.5,
                      }}
                    >
                      Tech & Tools
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        maxWidth: 720,
                      }}
                    >
                      {job.techStack.map((tech) => (
                        <Chip
                          key={tech}
                          label={tech}
                          size="small"
                          sx={{
                            backgroundColor: "rgba(100, 255, 218, 0.08)",
                            color: "#ccd6f6",
                            border: "1px solid rgba(100, 255, 218, 0.2)",
                            fontSize: "0.78rem",
                            fontFamily: "monospace",
                            "&:hover": {
                              backgroundColor: "rgba(100, 255, 218, 0.15)",
                              borderColor: "rgba(100, 255, 218, 0.5)",
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, lg: 5 }}>
                    <Box
                      sx={{
                        border: "1px solid rgba(100, 255, 218, 0.15)",
                        borderRadius: 2,
                        p: 3,
                        backgroundColor: "rgba(100, 255, 218, 0.03)",
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{
                          color: "#64ffda",
                          letterSpacing: "3px",
                          fontSize: "0.7rem",
                          display: "block",
                          mb: 2,
                        }}
                      >
                        Achievements
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                        }}
                      >
                        {job.achievements.map((achievement, i) => (
                          <Box
                            key={i}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1.5,
                            }}
                          >
                            <CheckCircleOutlinedIcon
                              sx={{
                                color: "#64ffda",
                                fontSize: "1.1rem",
                                mt: "3px",
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#a8b2d8",
                                lineHeight: 1.75,
                                fontSize: "0.93rem",
                              }}
                            >
                              {achievement}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                {index < jobs.length - 1 && (
                  <Divider
                    sx={{
                      mt: { xs: 6, md: 8 },
                      borderColor: "rgba(100, 255, 218, 0.1)",
                    }}
                  />
                )}
              </Box>
            ))}
          </>
        )}
      </Box>
    </Box>
  );
};

export default Experience;
